import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import { getBlogPostBySlug, getRelatedBlogPosts } from '../data/blog';

const categoryLabels: Record<string, string> = {
  handyvertrag: 'Handyvertrag',
  smartphone: 'Smartphone',
  internet: 'Internet',
  'strom-gas': 'Strom & Gas',
  ratgeber: 'Ratgeber',
};

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;
  const relatedPosts = slug ? getRelatedBlogPosts(slug) : [];

  if (!post) {
    return (
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center py-16">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Artikel nicht gefunden</h1>
            <p className="text-gray-600 mb-8">Der gesuchte Artikel existiert nicht.</p>
            <Link to="/blog" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
              <ArrowLeft className="mr-2 w-5 h-5" />
              Zum Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    const lines = post.content.split('\n');
    return lines.map((line, i) => {
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-3xl font-bold text-gray-800 mb-4 mt-6">{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-2xl font-bold text-gray-800 mb-3 mt-8">{line.slice(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-xl font-bold text-gray-800 mb-2 mt-6">{line.slice(4)}</h3>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="text-gray-700 mb-2 ml-6 list-disc">{line.slice(2)}</li>;
      }
      if (/^\d+\.\s/.test(line)) {
        return <li key={i} className="text-gray-700 mb-2 ml-6 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-4" />;
      }
      return <p key={i} className="text-gray-700 mb-4 leading-relaxed">{line}</p>;
    });
  };

  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>{post.title} | Easy Vertrag Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.keywords.join(', ')} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://easy4vertrag.de/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://easy4vertrag.de/blog/${post.slug}`} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "${post.title}",
            "description": "${post.excerpt}",
            "image": "${post.image}",
            "datePublished": "${post.date}",
            "author": {"@type": "Organization", "name": "${post.author}"},
            "publisher": {"@type": "Organization", "name": "Easy Vertrag", "url": "https://easy4vertrag.de"}
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Blog", "item": "https://easy4vertrag.de/blog"},
              {"@type": "ListItem", "position": 3, "name": "${post.title}", "item": "https://easy4vertrag.de/blog/${post.slug}"}
            ]
          }
        `}</script>
      </Helmet>

      <div className="container mx-auto px-4">
        <nav className="flex items-center text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-blue-600">Startseite</Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link to="/blog" className="hover:text-blue-600">Blog</Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-gray-700 truncate">{post.title}</span>
        </nav>

        <article className="max-w-3xl mx-auto">
          <div className="mb-6">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              {categoryLabels[post.category]}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 leading-tight">{post.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readingTime} Min Lesezeit
              </span>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden shadow-lg mb-8">
            <img src={post.image} alt={post.title} className="w-full h-auto" />
          </div>

          <div className="prose prose-lg max-w-none">
            {renderContent()}
          </div>

          <div className="mt-12 p-6 bg-blue-50 rounded-xl text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Noch Fragen?</h3>
            <p className="text-gray-600 mb-4">Lassen Sie sich kostenlos von uns beraten.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/angebote/tarif" className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                Tarife vergleichen <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
              <Link to="/kontakt" className="inline-flex items-center justify-center px-6 py-3 border-2 border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors">
                Kontakt aufnehmen
              </Link>
            </div>
          </div>
        </article>

        {relatedPosts.length > 0 && (
          <section className="max-w-5xl mx-auto mt-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Weitere Artikel</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((rp, i) => (
                <motion.div
                  key={rp.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link to={`/blog/${rp.slug}`} className="group block bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all">
                    <div className="h-40 overflow-hidden">
                      <img src={rp.image} alt={rp.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-gray-800 text-sm mb-2 group-hover:text-blue-600 transition-colors">{rp.title}</h3>
                      <span className="text-blue-600 text-sm font-medium inline-flex items-center group">
                        Weiterlesen <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BlogDetailPage;
